import { ref, computed } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";
import type { Announcement } from "@/types";

type DismissReason = "closed" | "link_clicked";

export const useAnnouncementsStore = defineStore("announcements", () => {
  const announcements = ref<Announcement[]>([]);
  const unread = ref<Announcement[]>([]);
  const unreadTotal = ref(0);
  const isUnreadFetched = ref(false);

  const current = computed(() => unread.value[0] ?? null);
  const unreadCount = computed(() => unread.value.length);
  const currentPosition = computed(() => unreadTotal.value - unread.value.length + 1);

  const fetchAnnouncements = async () => {
    const { data } = await useMyFetch("/announcements").json<{ announcements: Announcement[] }>();
    announcements.value = data.value?.announcements || [];
    return announcements.value;
  };

  const createAnnouncement = async (payload: Partial<Announcement>) => {
    const { data, error } = await useMyFetch("/announcements").post({ announcement: payload }).json<Announcement>();
    if (error.value) throw error.value;

    captureEvent("announcement_created", { announcement_id: data.value?.id });
    return data.value;
  };

  const updateAnnouncement = async (id: number, payload: Partial<Announcement>) => {
    const { data, error } = await useMyFetch(`/announcements/${id}`)
      .put({ announcement: payload })
      .json<Announcement>();
    if (error.value) throw error.value;

    captureEvent("announcement_updated", { announcement_id: id });
    return data.value;
  };

  const deleteAnnouncement = async (id: number) => {
    const { error } = await useMyFetch(`/announcements/${id}`).delete();
    if (error.value) throw error.value;

    captureEvent("announcement_deleted", { announcement_id: id });
    announcements.value = announcements.value.filter((announcement) => announcement.id !== id);
  };

  const fetchUnread = async () => {
    if (isUnreadFetched.value) return;
    isUnreadFetched.value = true;

    const { data, error } = await useMyFetch("/announcements?unread=true").json<{ announcements: Announcement[] }>();
    if (error.value) {
      isUnreadFetched.value = false;
      return;
    }

    unread.value = data.value?.announcements ?? [];
    unreadTotal.value = unread.value.length;
  };

  const dismissCurrent = async (reason: DismissReason) => {
    if (!current.value) return;

    const { id } = current.value;
    unread.value = unread.value.slice(1);
    if (current.value) captureEvent("announcement_viewed", { announcement_id: current.value.id });

    const { error } = await useMyFetch(`/announcements/${id}/dismissal`).post();
    if (error.value) return;

    if (reason === "closed") captureEvent("announcement_dismissed", { announcement_id: id });
    else captureEvent("announcement_link_clicked", { announcement_id: id });
  };

  return {
    announcements,
    current,
    currentPosition,
    unreadCount,
    unreadTotal,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    fetchUnread,
    dismissCurrent,
  };
});
